import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Plus, Trash2, X, Trophy, Calendar, ChevronDown, ChevronUp } from "lucide-react";

import { API_URL as API } from "../../config";

export default function TournamentsPage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showMatchForm, setShowMatchForm] = useState(null);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", team_ids: [], status: "upcoming" });
  const [matchForm, setMatchForm] = useState({ team1_id: "", team2_id: "", date: "", score1: "", score2: "", status: "scheduled" });
  const a = t.admin;

  const teamMap = {};
  teams.forEach(tm => { teamMap[tm.id] = tm; });

  const load = useCallback(async () => {
    const [tRes, teamsRes, mRes] = await Promise.all([
      authFetch(`${API}/api/admin/tournaments`),
      authFetch(`${API}/api/admin/teams`),
      authFetch(`${API}/api/admin/matches`),
    ]);
    if (tRes.ok) setTournaments(await tRes.json());
    if (teamsRes.ok) setTeams(await teamsRes.json());
    if (mRes.ok) setMatches(await mRes.json());
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const loadStandings = async (tId) => {
    const res = await authFetch(`${API}/api/admin/tournaments/${tId}/standings`);
    if (res.ok) {
      const data = await res.json();
      setStandings(prev => ({ ...prev, [tId]: data }));
    }
  };

  const toggleExpand = (tId) => {
    if (expanded === tId) { setExpanded(null); } else { setExpanded(tId); loadStandings(tId); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date) return;
    await authFetch(`${API}/api/admin/tournaments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setShowForm(false); setForm({ name: "", start_date: "", end_date: "", team_ids: [], status: "upcoming" }); load();
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    if (!matchForm.team1_id || !matchForm.team2_id || !matchForm.date) return;
    const body = { ...matchForm, tournament_id: showMatchForm, score1: matchForm.score1 !== "" ? parseInt(matchForm.score1) : null, score2: matchForm.score2 !== "" ? parseInt(matchForm.score2) : null };
    if (body.score1 !== null && body.score2 !== null) body.status = "completed";
    await authFetch(`${API}/api/admin/matches`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setShowMatchForm(null); setMatchForm({ team1_id: "", team2_id: "", date: "", score1: "", score2: "", status: "scheduled" }); load();
    if (expanded) loadStandings(expanded);
  };

  const updateMatchScore = async (matchId, s1, s2) => {
    await authFetch(`${API}/api/admin/matches/${matchId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ score1: parseInt(s1), score2: parseInt(s2), status: "completed" }) });
    load(); if (expanded) loadStandings(expanded);
  };

  const deleteMatch = async (matchId) => {
    if (!window.confirm(`${a.delete}?`)) return;
    await authFetch(`${API}/api/admin/matches/${matchId}`, { method: "DELETE" }); load();
  };

  const deleteTournament = async (tId) => {
    if (!window.confirm(`${a.delete}?`)) return;
    await authFetch(`${API}/api/admin/tournaments/${tId}`, { method: "DELETE" }); load();
  };

  const statusColors = { upcoming: "bg-blue-100 text-blue-700", active: "bg-emerald-100 text-emerald-700", completed: "bg-gray-100 text-gray-600" };

  return (
    <div data-testid="tournaments-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div><h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.tournaments}</h2>
          <p className="text-gray-500 text-sm">{tournaments.length} {a.tournaments.toLowerCase()}</p></div>
        <button onClick={() => setShowForm(true)} data-testid="add-tournament-btn"
          className="flex items-center gap-2 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
          <Plus className="w-4 h-4" /> {a.newTournament}
        </button>
      </div>

      {tournaments.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-400">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>{a.noTournaments}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tournaments.map(trn => {
            const tMatches = matches.filter(m => m.tournament_id === trn.id);
            const isExpanded = expanded === trn.id;
            return (
              <div key={trn.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid={`tournament-${trn.id}`}>
                <div className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50" onClick={() => toggleExpand(trn.id)}>
                  <div className="flex items-center gap-4">
                    <Trophy className="w-6 h-6 text-[#87CEEB]" />
                    <div>
                      <h3 className="font-bold font-['Kanit']">{trn.name}</h3>
                      <p className="text-xs text-gray-400">{trn.start_date}{trn.end_date ? ` - ${trn.end_date}` : ""} &middot; {(trn.team_ids || []).length} {a.teams.toLowerCase()} &middot; {tMatches.length} {a.matches.toLowerCase()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[trn.status] || "bg-gray-100"}`}>{trn.status}</span>
                    <button onClick={e => { e.stopPropagation(); deleteTournament(trn.id); }} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t p-5 space-y-4">
                    {standings[trn.id] && standings[trn.id].length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">{a.standings}</h4>
                        <table className="w-full text-sm">
                          <thead><tr className="text-left text-gray-500 text-xs"><th className="pb-2">#</th><th className="pb-2">{a.teams}</th><th className="pb-2">{a.played}</th><th className="pb-2">{a.wins}</th><th className="pb-2">{a.draws}</th><th className="pb-2">{a.losses}</th><th className="pb-2">{a.goalsFor}</th><th className="pb-2">{a.goalsAgainst}</th><th className="pb-2 font-bold">{a.points}</th></tr></thead>
                          <tbody>
                            {standings[trn.id].map((s, i) => (
                              <tr key={s.team_id} className="border-t border-gray-100">
                                <td className="py-2 text-gray-400">{i + 1}</td>
                                <td className="py-2 font-medium">{teamMap[s.team_id]?.name || s.team_id}</td>
                                <td className="py-2">{s.played}</td><td className="py-2">{s.wins}</td><td className="py-2">{s.draws}</td><td className="py-2">{s.losses}</td>
                                <td className="py-2">{s.goals_for}</td><td className="py-2">{s.goals_against}</td>
                                <td className="py-2 font-bold text-[#87CEEB]">{s.points}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-sm">{a.matches}</h4>
                        <button onClick={() => { setShowMatchForm(trn.id); setMatchForm({ team1_id: "", team2_id: "", date: "", score1: "", score2: "", status: "scheduled" }); }}
                          className="text-xs text-[#87CEEB] hover:underline font-medium">{a.addMatch}</button>
                      </div>
                      {tMatches.length === 0 ? <p className="text-sm text-gray-400">{a.noMatches}</p> : (
                        <div className="space-y-2">
                          {tMatches.map(m => (
                            <div key={m.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 text-sm">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="font-medium">{teamMap[m.team1_id]?.name || "?"}</span>
                                {m.status === "completed" ? (
                                  <span className="font-bold text-lg px-2">{m.score1} - {m.score2}</span>
                                ) : (
                                  <span className="text-gray-400 px-2">vs</span>
                                )}
                                <span className="font-medium">{teamMap[m.team2_id]?.name || "?"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{m.date}</span>
                                {m.status === "scheduled" && (
                                  <button onClick={() => {
                                    const s1 = prompt(`${a.score} ${teamMap[m.team1_id]?.name || a.team1}`);
                                    const s2 = prompt(`${a.score} ${teamMap[m.team2_id]?.name || a.team2}`);
                                    if (s1 !== null && s2 !== null) updateMatchScore(m.id, s1, s2);
                                  }} className="text-xs bg-[#87CEEB] text-black px-2 py-1 rounded-lg font-medium">{a.score}</button>
                                )}
                                <button onClick={() => deleteMatch(m.id)} className="p-1 hover:bg-red-100 rounded text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="tournament-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{a.newTournament}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.name} *</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="tournament-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.startDate} *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.endDate}</label>
                  <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.status}</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
                  <option value="upcoming">{a.upcoming}</option><option value="active">Active</option><option value="completed">{a.completed}</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">{a.teams} ({form.team_ids.length} {a.selected})</label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-xl divide-y">
                  {teams.map(tm => (
                    <label key={tm.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={form.team_ids.includes(tm.id)} onChange={() => setForm(prev => ({ ...prev, team_ids: prev.team_ids.includes(tm.id) ? prev.team_ids.filter(x => x !== tm.id) : [...prev.team_ids, tm.id] }))} className="rounded border-gray-300" />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tm.color }} /><span className="text-sm">{tm.name}</span>
                    </label>
                  ))}
                  {teams.length === 0 && <p className="px-3 py-4 text-center text-gray-400 text-sm">{a.createFirst}</p>}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">{a.cancel}</button>
                <button type="submit" data-testid="tournament-form-submit" className="flex-1 py-2.5 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black rounded-xl text-sm font-bold">{a.create}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMatchForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm" data-testid="match-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{a.addMatch}</h3>
              <button onClick={() => setShowMatchForm(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleAddMatch} className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.team1} *</label>
                <select value={matchForm.team1_id} onChange={e => setMatchForm({...matchForm, team1_id: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
                  <option value="">Select...</option>{teams.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.team2} *</label>
                <select value={matchForm.team2_id} onChange={e => setMatchForm({...matchForm, team2_id: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
                  <option value="">Select...</option>{teams.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.date} *</label>
                <input type="date" value={matchForm.date} onChange={e => setMatchForm({...matchForm, date: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.score} 1</label>
                  <input type="number" min="0" value={matchForm.score1} onChange={e => setMatchForm({...matchForm, score1: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.score} 2</label>
                  <input type="number" min="0" value={matchForm.score2} onChange={e => setMatchForm({...matchForm, score2: e.target.value})} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" /></div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowMatchForm(null)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">{a.cancel}</button>
                <button type="submit" data-testid="match-form-submit" className="flex-1 py-2.5 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black rounded-xl text-sm font-bold">{a.addMatch}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
